import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Hash, Pencil, Trash2, Plus, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = '';

export function AdminForumTopicsTab() {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [editTopic, setEditTopic] = useState(null);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/forum/topics`, { credentials: 'include' });
      if (res.ok) setTopics(await res.json());
      else toast.error('Failed to load forum topics');
    } catch { toast.error('Failed to load forum topics'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e?.preventDefault?.();
    const name = newName.trim();
    if (!name) { toast.error('Topic name is required'); return; }
    setCreating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/forum/topics`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed'); }
      toast.success(`Added "${name}"`);
      setNewName('');
      load();
    } catch (e) { toast.error(e.message); }
    setCreating(false);
  };

  const openEdit = (t) => { setEditTopic(t); setEditName(t.name); };

  const handleSaveRename = async () => {
    const name = editName.trim();
    if (!name) { toast.error('Topic name is required'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/forum/topics/${editTopic.topic_id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed'); }
      toast.success('Topic renamed');
      setEditTopic(null);
      load();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const handleDelete = async (t) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/forum/topics/${t.topic_id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed'); }
      const data = await res.json();
      toast.success(`Deleted "${data.topic_name}" — ${data.posts_untagged} post(s) untagged`);
      setDeleteConfirm(null);
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4" data-testid="admin-forum-topics-tab">
      <div>
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Hash className="h-4 w-4 text-brand-navy" /> Filter by Topic — Lookup
        </h3>
        <p className="text-sm text-slate-500">
          These topics power the Forum's "Filter by Topics" sidebar and the member profile "Topics of Interest" picker.
          Renaming a topic auto-updates every post tagged with it.
        </p>
      </div>

      <Card className="p-4">
        <form onSubmit={handleCreate} className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end">
          <div className="flex-1">
            <Label htmlFor="new-topic-name">Add new topic</Label>
            <Input id="new-topic-name" data-testid="new-topic-input" value={newName}
              onChange={e => setNewName(e.target.value)} placeholder="e.g. Reverse Logistics" className="mt-1" />
          </div>
          <Button type="submit" disabled={creating || !newName.trim()}
            className="bg-brand-navy text-white" data-testid="add-topic-btn">
            <Plus className="h-4 w-4 mr-1" /> {creating ? 'Adding...' : 'Add Topic'}
          </Button>
        </form>
      </Card>

      {loading ? (
        <Card className="p-8 text-center text-sm text-slate-500">Loading topics...</Card>
      ) : topics.length === 0 ? (
        <Card className="p-8 text-center">
          <Hash className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No forum topics yet</p>
        </Card>
      ) : (
        <Card className="divide-y" data-testid="forum-topics-list">
          {topics.map(t => (
            <div key={t.topic_id} data-testid={`topic-row-${t.topic_id}`}
              className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50">
              <div className="flex items-center gap-3 min-w-0">
                <Hash className="h-4 w-4 text-slate-400 flex-shrink-0" />
                <span className="font-medium text-slate-800 truncate">{t.name}</span>
                <span className="text-xs text-slate-400 flex items-center gap-1 flex-shrink-0">
                  <MessageSquare className="h-3 w-3" /> {t.post_count} post{t.post_count === 1 ? '' : 's'}
                </span>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button onClick={() => openEdit(t)} size="sm" variant="ghost"
                  data-testid={`rename-topic-${t.topic_id}`}
                  className="text-slate-500 hover:text-brand-navy hover:bg-brand-navy/10 h-8">
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                {deleteConfirm === t.topic_id ? (
                  <div className="flex gap-1.5 items-center">
                    <span className="text-xs text-red-600 font-medium">
                      Delete{t.post_count > 0 ? ` — ${t.post_count} post(s) will be untagged` : ''}?
                    </span>
                    <Button onClick={() => handleDelete(t)} size="sm" variant="destructive" className="h-7 px-2 text-xs"
                      data-testid={`confirm-delete-topic-${t.topic_id}`}>Yes</Button>
                    <Button onClick={() => setDeleteConfirm(null)} size="sm" variant="outline" className="h-7 px-2 text-xs"
                      data-testid={`cancel-delete-topic-${t.topic_id}`}>No</Button>
                  </div>
                ) : (
                  <Button onClick={() => setDeleteConfirm(t.topic_id)} size="sm" variant="ghost"
                    data-testid={`delete-topic-${t.topic_id}`}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </Card>
      )}

      <Dialog open={!!editTopic} onOpenChange={(open) => { if (!open) setEditTopic(null); }}>
        <DialogContent className="max-w-md" data-testid="rename-topic-dialog">
          <DialogHeader>
            <DialogTitle>Rename Topic</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label>Topic name</Label>
              <Input value={editName} onChange={e => setEditName(e.target.value)}
                data-testid="rename-topic-input" />
              <p className="text-xs text-slate-400 mt-1.5">
                Renaming will update every post currently tagged with "{editTopic?.name}".
              </p>
            </div>
            <Button onClick={handleSaveRename} disabled={saving || !editName.trim()}
              className="w-full bg-brand-navy text-white" data-testid="save-rename-btn">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
